import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { InputImage } from '../../../components/Inputs/InputImage';
import { useApi } from '../../../providers/ApiProvider';
import { GoodApiResponse, ErrorApiResponse } from '../../../types/api';
import { EntrepriseInfo } from '../../../types/auth';
import { Config } from '../../../config/config';
import { ReadOnlyText } from '../../../components/Inputs/ReadOnlyText';
import { handleError } from '../../../utils/ErrorHandler';
import { SubmitSave } from '../../../components/SubmitSave';

/**
 * Ce composant affiche la page mon entreprise qui répertorie les information de celle-ci pour le dirigeant.
 */
export const MonEntrepriseScreen: FunctionComponent = () => {
  /**
   * On stocke si on est en train d'utiliser l'API ou non.
   */
  const [apiIsLoading, setApiIsLoading] = useState(false);

  /**
   * On à besoin d'initialiser le client d'API à l'aide d'un hook personalisé.
   */
  const client = useApi();

  /**
   * Permet au composant de changer de page.
   */
  const navigate = useNavigate();

  /**
   * Contient un état général des valeurs des champs du formulaire ainsi que les champs qui ont reçu une erreur lors de l'envoi du formulaire.
   */
  const [formInputs, setFormInputs] = useState<{ [K in keyof EntrepriseInfo]: { value: EntrepriseInfo[K]; error: boolean } }>({
    raison_sociale: { value: '', error: false },
    enseigne: { value: null, error: false },
    image_logo: { value: null, error: false },
    adr1: { value: null, error: false },
    adr2: { value: null, error: false },
    adr3: { value: null, error: false },
    adr4: { value: null, error: false },
    code_postal: { value: null, error: false },
    ville: { value: null, error: false },
    pays: { value: null, error: false },
    siret: { value: null, error: false },
    est_approuvee: { value: 0, error: false },
  });

  /**
   * État local qui permet de stocker le logo de l'entreprise pour l'afficher sur la page.
   */
  const [imageLogoString, setImageLogoString] = useState<EntrepriseInfo['image_logo'] | null>(null);

  /**
   * Au chargement de la page on va chercher les données de l'entreprise.
   */
  useEffect(() => {
    handleShow();
  }, [client]);

  /**
   * Retrouve par le réseau la ressource liée à l'ID foruni et la met dans un état local.
   * Ne doit être utilisé qu'en mode édition et lecture seule.
   *
   * @param ressourceId - ID de la ressource dont on veut retrouver les informations.
   */
  const handleShow = () => {
    if (apiIsLoading === true) {
      return;
    }

    setApiIsLoading(true);
    client
      .get<GoodApiResponse<EntrepriseInfo>>('/entreprise/informations')
      .then((reponse) => {
        const updatedInputs = Object.keys(reponse.data.data).reduce((acc, key) => {
          if (key === 'image_logo' && reponse.data.data.image_logo !== null) {
            setImageLogoString(reponse.data.data.image_logo);
          }
          return { ...acc, [key]: { value: reponse.data.data[key as keyof EntrepriseInfo], error: false } };
        }, formInputs);
        setFormInputs(updatedInputs);
      })
      .catch((error: ErrorApiResponse) => {
        handleError(error, () => {
          navigate('/mon-espace');
        });
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Met à jour, par le réseau, une ressource à partir du formulaire via un état local.
   * Ne doit être utilisé qu'en mode édition.
   *
   * @param ressourceId - ID de la ressource dont on veut modifier les informations.
   */
  const handleUpdate = () => {
    setApiIsLoading(true);

    let ressource: Partial<EntrepriseInfo> = {};
    Object.keys(formInputs).forEach((key) => {
      if (formInputs[key as keyof EntrepriseInfo]?.value !== null && !(key === 'image_logo' && formInputs.image_logo.value === imageLogoString)) {
        ressource = { ...ressource, [key]: formInputs[key as keyof EntrepriseInfo]?.value };
      }
    });

    client
      .patch<GoodApiResponse<EntrepriseInfo>>(`/entreprise/informations`, ressource)
      .then(() => {
        toast.success(`Modification éffectuée`);
        navigate('/mon-espace');
      })
      .catch((error: ErrorApiResponse<EntrepriseInfo>) => {
        handleError(error, () => {
          let object = formInputs;
          Object.keys(error.response.data.errors).forEach((key) => {
            if (error.response.data.errors[key as keyof EntrepriseInfo]) {
              object = { ...object, [key]: { value: formInputs[key as keyof EntrepriseInfo]?.value, error: true } };
            }
          }, formInputs);
          setFormInputs(object);
        });
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Cette méthode permet de gérer l'envoi du formulaire en fonction du mode dans lequel on se trouve.
   * L'envoi ne fait rien si on est en mode voir.
   *
   * @param event - Évenement de l'envoie.
   */
  const handleSubmit = (event: { preventDefault: () => void }): void => {
    event.preventDefault();

    if (apiIsLoading === true) {
      return;
    }

    handleUpdate();
  };

  /**
   * Cette méthode permet de gérer le champ image.
   *
   * @param field - Nom du champ image.
   */
  const handleImageChange = (field: keyof EntrepriseInfo) => {
    return (imageString: string) => {
      setFormInputs((prev) => ({
        ...prev,
        [field]: { value: imageString, error: false },
      }));
    };
  };

  return (
    <Fragment>
      <Helmet>
        <title>Mes informations - {Config.app_label}</title>
      </Helmet>
      <div className='px-4 py-4 md:py-3 bg-gray-100 rounded-tl-lg rounded-tr-lg flex flex-col sm:flex-row gap-7 items-center'>
        <h1 className='text-base sm:text-lg md:text-xl poppins-bold leading-normal text-secondary-1'>Mon entreprise</h1>
      </div>
      <form className='relative' onSubmit={handleSubmit}>
        <div className='bg-white p-5 rounded-b-lg'>
          <div className='container mx-auto bg-white rounded'>
            <fieldset className='pb-7 mb-7 border-b'>
              <legend className='block text-left mb-3 text-xl text-secondary-1'>Informations</legend>
              <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 items-center'>
                <div>
                  <ReadOnlyText label='Raison sociale' value={formInputs.raison_sociale.value} error={formInputs.raison_sociale.error} />
                </div>
                <div>
                  <ReadOnlyText label='Enseigne' value={formInputs.enseigne.value} error={formInputs.enseigne.error} />
                </div>
                <div>
                  <ReadOnlyText label='SIRET' value={formInputs.siret.value} error={formInputs.siret.error} />
                </div>
                <div>
                  <ReadOnlyText
                    label='Est approuvé'
                    value={formInputs.est_approuvee.value === 1 ? 'Oui' : formInputs.est_approuvee.value === 2 ? 'Non' : 'Non défini'}
                    error={formInputs.est_approuvee.error}
                  />
                </div>
              </div>
            </fieldset>
            <fieldset className='pb-7 mb-7 border-b'>
              <legend className='block text-left mb-3 text-xl text-secondary-1'>Adresse</legend>
              <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 items-center'>
                <div>
                  <ReadOnlyText label='Adresse 1' value={formInputs.adr1.value} error={formInputs.adr1.error} />
                </div>
                <div>
                  <ReadOnlyText label='Adresse 2' value={formInputs.adr2.value} error={formInputs.adr2.error} />
                </div>
                <div>
                  <ReadOnlyText label='Adresse 3' value={formInputs.adr3.value} error={formInputs.adr3.error} />
                </div>
                <div>
                  <ReadOnlyText label='Adresse 4' value={formInputs.adr4.value} error={formInputs.adr4.error} />
                </div>
                <div>
                  <ReadOnlyText label='Code postal' value={formInputs.code_postal.value} error={formInputs.code_postal.error} />
                </div>
                <div>
                  <ReadOnlyText label='Ville' value={formInputs.ville.value} error={formInputs.ville.error} />
                </div>
                <div>
                  <ReadOnlyText label='Pays' value={formInputs.pays.value} error={formInputs.pays.error} />
                </div>
              </div>
            </fieldset>
            <fieldset className='pb-7 mb-7 border-b'>
              <legend className='block text-left mb-3 text-xl text-secondary-1'>Logo</legend>
              <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2'>
                <div>
                  <InputImage label='' imageUrl={imageLogoString as string} onChange={handleImageChange('image_logo')} />
                </div>
              </div>
            </fieldset>
          </div>

          <div className='container mx-auto w-11/12 xl:w-full'>
            <div className='w-full py-4 sm:px-0 bg-white flex justify-end'>
              <SubmitSave />
            </div>
          </div>
        </div>
      </form>
    </Fragment>
  );
};
